import { PaginatedResponse } from '../../../types/index';
import { ReactQueryConfig } from 'hooks/hooks.types';
import { useInfiniteAxiosQuery } from 'hooks/useInfiniteAxiosQuery';
import { useEffect, useRef } from 'react';
import { ORGANISATIONS_CACHE_KEY } from 'services/queryKeys';
import { Organisation } from 'types';

export const useGetOrganisations = ({
  perPage = 12,
  accumulateData = false,
  reactQueryConfig = {},
  searchTerm = '',
  order = 'asc',
  orderBy = 'name',
  filters = [],
}: {
  perPage?: number;
  accumulateData?: boolean;
  reactQueryConfig?: ReactQueryConfig<PaginatedResponse<Organisation>>;
  searchTerm?: string;
  order?: string;
  orderBy?: string;
  filters?: { key: string; value: string | number | boolean }[];
} = {}) => {
  const filterParams = new URLSearchParams(
    Object.fromEntries([
      ...(searchTerm ? [['name__contains', searchTerm]] : []),
      ...filters.map(({ key, value }) => [key, String(value)]),
      ['order', order],
      ['order_by', orderBy],
    ])
  );
  const queryParams = filterParams.toString();

  const {
    data,
    isSuccess,
    isError,
    isFetching,
    goToNextPage,
    goToPreviousPage,
    resetPage,
    refetch,
    currentPage,
    ...rest
  } = useInfiniteAxiosQuery<Organisation>({
    key: ORGANISATIONS_CACHE_KEY(perPage, searchTerm, filters),
    path: `/organisations/?${queryParams}`,
    accumulateData,
    perPage,
    reactQueryConfig,
  });

  const prevSearchRef = useRef(searchTerm);

  useEffect(() => {
    if (prevSearchRef.current !== searchTerm) {
      resetPage();
      prevSearchRef.current = searchTerm;
    }
  }, [searchTerm, resetPage]);

  return {
    organisations: data,
    isSuccess,
    isError,
    isFetching,
    goToNextPage,
    goToPreviousPage,
    refetch,
    currentPage,
    resetPage,
    ...rest,
  };
};
